import { EXPLORER_CHART_BLUE } from 'features/drilldown/colors'
import { HistoStackingGroup } from 'features/drilldown/components/histogram/configPanel/histoStackingTypes'
import {
  DrilldownPlotType,
  HistogramPanelData,
  MAX_NUMBER_OF_HISTOGRAM_ROWS,
  SortDirection,
} from 'features/drilldown/components/histogram/DrilldownHistogram'
import {
  HISTOGRAM_DATE_BUCKETING_OPTIONS,
  HistogramDateBucketingConfiguration,
  HistogramDateBucketingOption,
} from 'features/drilldown/components/histogram/histogramDateConfigurationOptions'
import { PlotlyOrientation } from 'features/drilldown/components/histogram/plotlyGenerators'
import { useVersionAwareDrilldownPanelData } from 'features/drilldown/data/DrilldownPanelDataContext'
import { useNestedSetter } from 'features/drilldown/utils/useNestedSetter'
import {
  OptionalValueSetterPair,
  ValueSetterPair,
} from 'features/drilldown/utils/valueSetterPair'
import { OntologyField, useOntology } from 'features/dynamicOntology'
import {
  createContext,
  FC,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
} from 'react'
import { RolodexHistogramAggregationOp } from 'types'

export const DEFAULT_SORT_DIRECTION = 'desc'

export type HistogramConfigurationContextType = OptionalValueSetterPair<
  PlotlyOrientation | undefined,
  'orientation'
> &
  ValueSetterPair<ReadonlyArray<[string, number | null]>, 'selectedBarIds'> &
  OptionalValueSetterPair<OntologyField, 'groupingColumn'> &
  OptionalValueSetterPair<OntologyField, 'aggregationColumn'> &
  OptionalValueSetterPair<RolodexHistogramAggregationOp, 'aggregationOp'> &
  ValueSetterPair<string | undefined, 'color'> &
  OptionalValueSetterPair<DrilldownPlotType | undefined, 'plotType'> &
  OptionalValueSetterPair<SortDirection | undefined, 'sortDirection'> &
  OptionalValueSetterPair<
    HistogramDateBucketingConfiguration | undefined,
    'dateBucketingConfiguration'
  > &
  OptionalValueSetterPair<
    ReadonlyArray<HistoStackingGroup> | undefined,
    'stackingGroups'
  > &
  OptionalValueSetterPair<number | undefined | null, 'truncateCount'>

const HistogramConfigurationContext = createContext<
  HistogramConfigurationContextType | undefined
>(undefined)

export type HistogramConfigurationProviderProps = {
  defaultPanelData: HistogramPanelData
  latestDataSchemaVersion: number
  isSortDirectionConfigurable?: boolean
  isDateBucketingConfigurable?: boolean
  isPlotTypeConfigurable?: boolean
  isOrientationConfigurable?: boolean
  isStackable?: boolean
  isGroupingColumnConfigurable?: boolean
  isAggregationColumnConfigurable?: boolean
  isAggregationOpConfigurable?: boolean
}

export const DEFAULT_HISTOGRAM_COLOR = EXPLORER_CHART_BLUE
const DEFAULT_DATE_BUCKETING_PERIOD: HistogramDateBucketingOption = 'quarter'

export const STACKING_GROUP_OTHER: HistoStackingGroup = {
  id: '@@other',
  name: 'Others',
  color: DEFAULT_HISTOGRAM_COLOR.trim(),
  enabled: true,
  filters: [],
}

export const HistogramConfigurationContextProvider: FC<
  HistogramConfigurationProviderProps & { children: ReactNode }
> = ({
  isSortDirectionConfigurable = true,
  isDateBucketingConfigurable = false,
  isPlotTypeConfigurable = true,
  isOrientationConfigurable = true,
  isStackable = true,
  isGroupingColumnConfigurable = true,
  isAggregationColumnConfigurable = true,
  isAggregationOpConfigurable = true,
  children,
}) => {
  const { panelData, setPanelData } =
    useVersionAwareDrilldownPanelData<HistogramPanelData>()

  const {
    selectedBarIds,
    groupingColumn,
    aggregationColumn,
    orientation = 'h',
    sortDirection = DEFAULT_SORT_DIRECTION,
    color = DEFAULT_HISTOGRAM_COLOR,
    plotType = 'bar',
    dateBucketingConfiguration = HISTOGRAM_DATE_BUCKETING_OPTIONS[
      DEFAULT_DATE_BUCKETING_PERIOD
    ],
    stackingGroups,
    truncateCount = MAX_NUMBER_OF_HISTOGRAM_ROWS,
    aggregationOperation,
  } = panelData

  const { fields } = useOntology()
  const setColor = useNestedSetter(setPanelData, 'color')
  const setOrientation = useNestedSetter(setPanelData, 'orientation')
  const setSortDirection = useNestedSetter(setPanelData, 'sortDirection')
  const setSelectedBarIds = useNestedSetter(setPanelData, 'selectedBarIds')
  const setGroupingColumn = useNestedSetter(
    setPanelData,
    'groupingColumn',
    (col) => Object.keys(fields).includes(col)
  )
  const setAggregationColumn = useNestedSetter(
    setPanelData,
    'aggregationColumn',
    (col) => Object.keys(fields).includes(col)
  )
  const setDateBucketingConfiguration = useNestedSetter(
    setPanelData,
    'dateBucketingConfiguration'
  )
  const setPlotType = useNestedSetter(setPanelData, 'plotType')
  const setStackingGroups = useNestedSetter(setPanelData, 'stackingGroups')
  const setTruncateCount = useNestedSetter(setPanelData, 'truncateCount')
  const setAggregationOp = useNestedSetter(setPanelData, 'aggregationOperation')

  // Look up dateBucketingConfiguration in order to get the bucketing function which isn't serialisable, eg after loading an exploration
  const dateConfigurationWithBucketingFunction = useMemo(() => {
    return HISTOGRAM_DATE_BUCKETING_OPTIONS[
      dateBucketingConfiguration?.value ?? DEFAULT_DATE_BUCKETING_PERIOD
    ]
  }, [dateBucketingConfiguration])

  useEffect(() => {
    setStackingGroups((prev) => (!prev?.length ? [STACKING_GROUP_OTHER] : prev))
  }, [setStackingGroups, stackingGroups])

  // If the field changes, and it's not a number, set the op back to count
  const aggFieldDef = fields[aggregationColumn]
  useEffect(
    () =>
      setAggregationOp((prev) =>
        aggFieldDef?.type !== 'number' ? 'count' : prev
      ),
    [aggFieldDef?.type, setAggregationOp]
  )

  const contextValue = useMemo<HistogramConfigurationContextType>(
    () => ({
      selectedBarIds,
      setSelectedBarIds,
      groupingColumn,
      setGroupingColumn: isGroupingColumnConfigurable
        ? setGroupingColumn
        : undefined,
      color,
      setColor,
      orientation,
      setOrientation: isOrientationConfigurable ? setOrientation : undefined,
      sortDirection,
      setSortDirection: isSortDirectionConfigurable
        ? setSortDirection
        : undefined,
      aggregationColumn,
      setAggregationColumn: isAggregationColumnConfigurable
        ? setAggregationColumn
        : undefined,
      dateBucketingConfiguration: dateConfigurationWithBucketingFunction,
      setDateBucketingConfiguration: isDateBucketingConfigurable
        ? setDateBucketingConfiguration
        : undefined,
      plotType,
      setPlotType: isPlotTypeConfigurable ? setPlotType : undefined,
      stackingGroups,
      setStackingGroups: isStackable ? setStackingGroups : undefined,
      truncateCount,
      setTruncateCount,
      aggregationOp: aggregationOperation,
      setAggregationOp: isAggregationOpConfigurable
        ? setAggregationOp
        : undefined,
    }),
    [
      aggregationColumn,
      aggregationOperation,
      color,
      dateConfigurationWithBucketingFunction,
      groupingColumn,
      isAggregationColumnConfigurable,
      isAggregationOpConfigurable,
      isDateBucketingConfigurable,
      isGroupingColumnConfigurable,
      isOrientationConfigurable,
      isPlotTypeConfigurable,
      isSortDirectionConfigurable,
      isStackable,
      orientation,
      plotType,
      selectedBarIds,
      setAggregationColumn,
      setAggregationOp,
      setColor,
      setDateBucketingConfiguration,
      setGroupingColumn,
      setOrientation,
      setPlotType,
      setSelectedBarIds,
      setSortDirection,
      setStackingGroups,
      setTruncateCount,
      sortDirection,
      stackingGroups,
      truncateCount,
    ]
  )

  return (
    <HistogramConfigurationContext.Provider value={contextValue}>
      {children}
    </HistogramConfigurationContext.Provider>
  )
}

export function useHistogramConfiguration(): HistogramConfigurationContextType {
  const configuration = useContext(HistogramConfigurationContext)
  if (!configuration) {
    throw new Error(
      `Can only use histogram config inside a histogram config context`
    )
  }
  return configuration
}
